// src/shaders/waterShader.js

export const vertexShader = `
  varying vec3 vWorldPosition;

  void main() {
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPosition.xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const fragmentShader = `
  #ifdef GL_ES
  precision mediump float;
  #endif

  uniform float u_time;
  // uniform vec2 u_mouse; // Mouse uniform not used for now
  uniform vec2 u_resolution;

  // Uniforms replacing constants for control
  uniform float uSeaHeight;
  uniform float uSeaChoppy;
  uniform float uSeaSpeed;
  uniform float uSeaFreq;
  uniform vec3 uSeaBaseColor;
  uniform vec3 uSeaWaterColor;
  uniform float uSkyIntensity;

  varying vec3 vWorldPosition;

  const int NUM_STEPS = 8;
  const float PI = 3.1415;
  const float EPSILON = 1e-3;

  // sea
  const int ITER_GEOMETRY = 3;
  const int ITER_FRAGMENT = 5;

  // math
  mat4 fromEuler(vec3 ang) {
    vec2 a1 = vec2(sin(ang.x),cos(ang.x));
    vec2 a2 = vec2(sin(ang.y),cos(ang.y));
    vec2 a3 = vec2(sin(ang.z),cos(ang.z));
    mat4 m;
    m[0] = vec4(a1.y*a3.y+a1.x*a2.x*a3.x,a1.y*a2.x*a3.x+a3.y*a1.x,-a2.y*a3.x,0.0);
    m[1] = vec4(-a2.y*a1.x,a1.y*a2.y,a2.x,0.0);
    m[2] = vec4(a3.y*a1.x*a2.x+a1.y*a3.x,a1.x*a3.x-a1.y*a3.y*a2.x,a2.y*a3.y,0.0);
    m[3] = vec4(0.0,0.0,0.0,1.0);
    return m;
  }

  float hash( vec2 p ) {
    float h = dot(p,vec2(127.1,311.7));	
    return fract(sin(h)*43758.5453123);
  }

  float noise( in vec2 p ) {
    vec2 i = floor( p );
    vec2 f = fract( p );	
    vec2 u = f*f*(3.0-2.0*f);
    return -1.0+2.0*mix( mix( hash( i + vec2(0.0,0.0) ), 
                      hash( i + vec2(1.0,0.0) ), u.x),
                mix( hash( i + vec2(0.0,1.0) ), 
                      hash( i + vec2(1.0,1.0) ), u.x), u.y);
  }

  // lighting
  float diffuse(vec3 n,vec3 l,float p) { return pow(dot(n,l) * 0.4 + 0.6,p); }
  float specular(vec3 n,vec3 l,vec3 e,float s) {    
    float nrm = (s + 8.0) / (PI * 8.0);
    return pow(max(dot(reflect(e,n),l),0.0),s) * nrm;
  }

  // sky (simplified, reflection only)
  vec3 sky_color(vec3 e) {
    e.y = max(e.y,0.0);
    vec3 ret;
    ret.x = pow(1.0-e.y,2.0);
    ret.y = 1.0-e.y;
    ret.z = 0.6+(1.0-e.y)*0.4;
    return ret * uSkyIntensity;
  }

  // sea
  float sea_octave(vec2 uv, float choppy) {
    uv += noise(uv);
    vec2 wv = 1.0-abs(sin(uv));
    vec2 swv = abs(cos(uv));
    wv = mix(wv,swv,wv);
    return pow(1.0-pow(wv.x * wv.y,0.65),choppy);
  }

  float map(vec3 p) {
    float freq = uSeaFreq;
    float amp = uSeaHeight;
    float choppy = uSeaChoppy;
    vec2 uv = p.xz; uv.x *= 0.75; // Use world position xz
    mat2 m = mat2(1.6,1.2,-1.2,1.6);
    float seaTime = u_time * uSeaSpeed;
    
    float d, h = 0.0;    
    for(int i = 0; i < ITER_GEOMETRY; i++) {        
      d = sea_octave((uv+seaTime)*freq,choppy);
      d += sea_octave((uv-seaTime)*freq,choppy);
      h += d * amp;
      uv *= m; freq *= 1.9; amp *= 0.22;
      choppy = mix(choppy,1.0,0.2);
    }
    return p.y - h;
  }

  float map_detailed(vec3 p) {
    float freq = uSeaFreq;
    float amp = uSeaHeight;
    float choppy = uSeaChoppy;
    vec2 uv = p.xz; uv.x *= 0.75; // Use world position xz
    mat2 m = mat2(1.6,1.2,-1.2,1.6);
    float seaTime = u_time * uSeaSpeed;
    
    float d, h = 0.0;    
    for(int i = 0; i < ITER_FRAGMENT; i++) {
      d = sea_octave((uv+seaTime)*freq,choppy);
      d += sea_octave((uv-seaTime)*freq,choppy);
      h += d * amp;
      uv *= m; freq *= 1.9; amp *= 0.22;
      choppy = mix(choppy,1.0,0.2);
    }
    return p.y - h;
  }

  vec3 getNormal(vec3 p, float eps) {
    vec3 n;
    n.y = map_detailed(p);
    n.x = map_detailed(vec3(p.x+eps,p.y,p.z)) - n.y;
    n.z = map_detailed(vec3(p.x,p.y,p.z+eps)) - n.y;
    n.y = eps;
    return normalize(n);
  }

  // sea_color adjusted for world space ray calculation
  vec3 sea_color(vec3 p, vec3 n, vec3 eye_dir, float dist_factor) {
    float fresnel_o = 1.0 - max(dot(n, -eye_dir), 0.0);
    float fresnel = pow(fresnel_o, 3.0) * 0.65;

    vec3 reflected_dir = reflect(eye_dir, n);
    vec3 refl = sky_color(reflected_dir);

    vec3 base = uSeaBaseColor;
    vec3 water = uSeaWaterColor;

    vec3 ret = base;
    ret = mix(ret, refl, fresnel);

    float atten = max(1.0 - dist_factor * 0.001, 0.0);
    ret += water * (p.y - uSeaHeight) * 0.18 * atten; // Use uSeaHeight

    return ret;
  }

  void main(void) {
    vec3 V = normalize(vWorldPosition - cameraPosition); // View direction
    vec3 N = getNormal(vWorldPosition, 0.1 / u_resolution.x);
    
    // Use world position directly instead of raymarching from camera
    vec3 P = vWorldPosition;
    float distFactor = length(P - cameraPosition);
    
    vec3 color = sea_color(P, N, V, distFactor);

    // Basic Lighting (can be improved)
    vec3 light = normalize(vec3(0.0, 1.0, 0.8)); // Simplified light direction
    color += vec3(diffuse(N, light, 80.0) * uSeaWaterColor) * 0.12;
    color += vec3(specular(N, light, V, 60.0));

    // Basic Sky fallback
    // color = mix(sky_color(V), color, pow(smoothstep(0.0, 0.05, P.y / uSeaHeight), 0.3)); // Mix based on height?
    
    color = pow(color, vec3(0.75));
    gl_FragColor = vec4(color, 1.0);
  }
`; 